import React, { ErrorInfo, ReactNode } from "react";
import { ChevronLeft, House, RefreshCw } from "lucide-react";
import LogoPinyadaColor from "../assets/theme/images/logo-pinyada-color.png";
import type { NavigateFunction } from "react-router-dom";

interface Props {
  children: ReactNode;
  navigate?: NavigateFunction;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isChunkLoadError =
      error.name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/.test(error.message) ||
      /ChunkLoadError/.test(error.message);

    if (isChunkLoadError) {
      const reloadKey = "__chunk_reload_once__";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "true");
        console.warn(
          "🔁 ErrorBoundary -> ChunkLoadError detected. Reloading page..."
        );
        window.location.reload();
      }
      return;
    }

    // For other errors, set state so fallback UI shows
    this.setState({ hasError: true, error });

    // Optional: Pass error to external logger
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
  };

  renderFallbackUI() {
    const { error } = this.state;

    return (
      <div className="w-full px-1 bg-[#fcd2d7] min-h-screen">
        <div className="relative w-full mx-auto px-3 md:px-7 lg:px-6 xl:px-8 py-4 lg:py-4">
          <div className="flex items-center justify-between">
            <div className="error-page-logo">
              <div className="bg-white/70 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-lg shadow-md flex items-center space-x-3">
                <img
                  src={LogoPinyadaColor}
                  alt="Pinyada Logo"
                  className="w-[8rem] h-auto md:w-[10rem] md:h-auto"
                  onError={(e) => {
                    console.warn("Logo image failed to load");
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center text-center h-full bg-[#fcd2d7] px-2 pt-[6rem] pb-[6rem] w-full mx-auto">
          <h1 className="text-2xl font-bold text-red-700 mb-2">
            Something went wrong.
          </h1>
          <p className="text-gray-700 mb-4">
            We encountered an unexpected error:
          </p>

          <div className="w-full md:w-auto mx-1">
            <pre className="bg-red-100 text-sm text-center p-3 rounded max-w-sm md:max-w-full overflow-auto text-red-800">
              {error?.message || "An unknown error occurred."}
            </pre>
          </div>

          {process.env.NODE_ENV === "development" && error?.stack && (
            <details className="mt-4 text-left text-sm max-w-sm md:max-w-full w-full md:w-auto">
              <summary className="cursor-pointer font-medium text-red-600">
                Stack Trace
              </summary>
              <pre className="bg-red-100 mt-2 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="mt-6 flex gap-4 flex-wrap justify-center">
            <button
              onClick={() => window.history.back()}
              className="flex gap-2 px-4 py-3 bg-gray-600 text-white rounded hover:bg-gray-700 rounded-full shadow-md hover:scale-[0.9] transition-transform duration-200"
            >
              <ChevronLeft className="w-4" /> Go Back
            </button>

            <a
              href="/"
              className="flex gap-2 px-4 py-3 rounded bg-black text-white rounded-full shadow-md hover:scale-[0.9] transition-transform duration-200"
            >
              <House className="w-4" /> Go to Home
            </a>

            <button
              onClick={this.resetErrorBoundary}
              className="flex gap-2 px-4 py-3 bg-red-700 text-white rounded-full shadow-md hover:bg-red-800 hover:scale-[0.9] transition-transform duration-200"
            >
              <RefreshCw className="w-4" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { hasError } = this.state;

    if (hasError) {
      try {
        return this.renderFallbackUI();
      } catch (fallbackError) {
        console.error("Error rendering fallback UI", fallbackError);
        return (
          <div className="flex items-center justify-center h-screen bg-white text-red-700 font-semibold text-lg px-4 text-center">
            A fatal error occurred and we couldn’t recover.
          </div>
        );
      }
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
